import * as React from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import Button from '@app/react-common/components/button/Button';
import useCheckoutGroupSettings from './hooks/useCheckoutGroupSettings';
import {
  CheckoutGroupSettings,
  DeliveryMethodSortingSettings,
  DisplayOptimisationSettings,
} from '../../models/CheckoutGroupSettings';
import { DeliveryMethodSortingSettingsView } from './components/delivery-method-sorting/DeliveryMethodSortingSettingsView';
import { DisplayOptimisationSettingsView } from './components/display-optimisation-settings/DisplayOptimisationSettingsView';
import { NotCountedAsBusinessDays } from './components/not-counted-as-business-days/NotCountedAsBusinessDays';
import useCheckoutSettings from '@pages/checkout-app/checkout-layout/main/components/checkout/react/features/checkout-settings-modal/hooks/useCheckoutSettings';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';

interface CheckoutSettingsModalProps {
  shippingMethodGroupId: number;
  isOpen;
  onClose;
  onSaveCheckoutSettingsSuccess?: (checkoutGroupSettings: CheckoutGroupSettings) => Promise<void>;
}

export const CheckoutSettingsModal: React.FC<CheckoutSettingsModalProps> = (props: CheckoutSettingsModalProps) => {
  const { checkoutGroupSettings, setCheckoutGroupSettings } = useCheckoutGroupSettings(props.shippingMethodGroupId);
  const { checkoutSettings } = useCheckoutSettings();

  const handleClose = () => {
    props.onClose();
  };

  return (
    <Modal isOpen={props.isOpen} onClose={handleClose}>
      <div className="checkout-settings-modal">
        <h2 className="checkout-settings-modal__title">Settings</h2>
        {checkoutGroupSettings && (
          <>
            <DisplayOptimisationSettingsView
              checkoutSettings={checkoutSettings}
              displayOptimisationSettings={checkoutGroupSettings?.displayRatesOptimization}
              onChange={(displayOptimisationSettings: DisplayOptimisationSettings) => {
                setCheckoutGroupSettings({
                  ...checkoutGroupSettings,
                  displayRatesOptimization: displayOptimisationSettings,
                });
              }}
            />
            <div className="checkout-settings-modal__splitter"></div>
            <DeliveryMethodSortingSettingsView
              deliveryMethodSortingSettings={checkoutGroupSettings?.deliveryMethodSorting}
              checkoutSettings={checkoutSettings}
              onChange={(settings: DeliveryMethodSortingSettings) => {
                setCheckoutGroupSettings({ ...checkoutGroupSettings, deliveryMethodSorting: settings });
              }}
            />
            <div className="checkout-settings-modal__splitter"></div>
            <NotCountedAsBusinessDays
              existingItems={checkoutGroupSettings?.nonBusinessDays || []}
              onChange={(items: Checkout.NonBusinessDaysItem[]) => {
                setCheckoutGroupSettings({
                  ...checkoutGroupSettings,
                  nonBusinessDays: items,
                });
              }}
            />
          </>
        )}
      </div>
      <div className="checkout-settings-modal__actions">
        <Button
          onClick={handleClose}
          text={'Cancel'}
          size={'medium'}
          variant={'secondary'}
          className="checkout-settings-modal__actions__cancel"
        />
        <Button
          onClick={async () => {
            await props.onSaveCheckoutSettingsSuccess(checkoutGroupSettings);
          }}
          text={'Save'}
          size={'medium'}
          variant={'primary'}
        />
      </div>
    </Modal>
  );
};
